// Constants only.
//
// The layout imports this module statically, so a `maplibre-gl` import or a
// `setWorkerUrl()` call here would drag 304 KB of WebGL code into the entry
// bundle and evaluate it during a server render — defeating the dynamic import
// of atlas_map.tsx that KTD16 exists to protect. Everything that touches the
// library lives in components/map/use_maplibre.ts.
//
// The pure helpers at the bottom are the exception the header allows: they are
// string formatting over numbers, with no import of anything but a type.

import type { MapViewport } from '../types/atlas'

// KTD8. The basemap is OpenFreeMap: no key, no registration, which is what R12
// asks for. Self-hosted Protomaps PMTiles is the documented fallback, and P3
// owns it because it needs somewhere to put a 188 MB archive.
//
// What the map draws is not OpenFreeMap's hosted `liberty` style. It is the
// atlas's own style over the same tiles and the same glyph endpoint, built in
// lib/atlas_style.ts and bundled into the map chunk: `liberty` is a street map
// (111 layers, a raster hillshade, a sprite, labels in every script) and cost
// 3.2 MB on the world view, where the atlas needs quiet paper for the
// appellations to be drawn on. That module is imported from use_maplibre.ts
// only, never from here — this file is in the entry bundle and the style is not.
//
// So no style URL is exported from here any more: nothing requests one. The
// only OpenFreeMap addresses the app holds are the TileJSON and glyph endpoints
// inside the bundled style.
export const BASEMAP_NAME = 'OpenFreeMap'

export interface AttributionPart {
  label: string
  href?: string
  /**
   * Said after the part that names and links its subject, and the one part a
   * narrow row may leave off: the relief's list of contributors, which the
   * link before it leads to in full.
   */
  tail?: boolean
}

// Rendered verbatim, joined by single spaces:
// "OpenFreeMap © OpenMapTiles Data from OpenStreetMap", each named party linked.
// The ODbL requires it to stay visible, which is why the chrome moves around the
// panel rather than being covered by it.
//
// An `href` that starts with `/` is a page of this app (the relief's credit
// has one); anything else is somebody else's site and opens in a new tab.
export const BASEMAP_ATTRIBUTION: readonly AttributionPart[] = [
  { label: 'OpenFreeMap', href: 'https://openfreemap.org' },
  { label: '©' },
  { label: 'OpenMapTiles', href: 'https://openmaptiles.org' },
  { label: 'Data from' },
  { label: 'OpenStreetMap', href: 'https://www.openstreetmap.org/copyright' },
]

// Camera. Centred west of the Greenwich meridian and low enough that the whole
// wine world is on screen at first paint.
export const INITIAL_CENTER: [number, number] = [5, 25]
export const INITIAL_ZOOM = 1.3
export const MAX_ZOOM = 14

// THE SLOTS: seven layers that draw nothing and exist to be inserted in front
// of, so that an atlas layer lands at a fixed depth in the basemap without
// naming a basemap layer. What each depth is, and why there are seven, is
// components/map/slots.ts, which re-exports these; the ids are here because
// this is where layer ids live, and because one module outside the map's
// chunk names two of them (components/map/reader_mark.ts).
export const SLOT_RELIEF = 'atlas-slot-relief'
export const SLOT_TINT = 'atlas-slot-tint'
export const SLOT_BAND = 'atlas-slot-band'
export const SLOT_LINE = 'atlas-slot-line'
export const SLOT_HIGHLIGHT = 'atlas-slot-highlight'
export const SLOT_DOT = 'atlas-slot-dot'
export const SLOT_LABEL = 'atlas-slot-label'

// Source and layer ids for the atlas geometry drawn into the basemap. "Into",
// not "on top of": every one of these layers is inserted before one of the
// named slots the style carries (components/map/slots.ts), so a tint sits under
// the basemap's water and roads and a label sits above its town names.
export const ATLAS_SOURCE_ID = 'atlas-world'
export const ATLAS_FILL_LAYER_ID = 'atlas-world-fill'

// A country's two edge marks, one per state that has one. They were a single
// outline, `atlas-world-line`, in three colours: the difference between "drawn"
// and "held as names" is now carried by the KIND of mark — a solid ribbon
// inside the coast against a dashed line — because relief shading is umber and
// soft and a second tint beside it does not read as a second state.
export const ATLAS_RIBBON_LAYER_ID = 'atlas-world-ribbon'
export const ATLAS_NAMED_LINE_LAYER_ID = 'atlas-world-named'

// ONE INK. Every mark the atlas makes about a place — a country's tint, an
// appellation's hairline, ribbon and name — is this claret (`--color-accent` in
// application.css) at one weight or another. What differs between two states is
// strength and the kind of mark, never the hue: the three hues the country
// layer used to have are gone, and their constants below all hold this value so
// the key can go on reading them by name.
export const COUNTRY_FILL_COLOR = '#7f1d3f'

// The solid ribbon inside a drawn country's coast.
export const COUNTRY_LINE_COLOR = '#7f1d3f'

// What a country's shading says, mirroring `MapProps::COVERAGE_*` on the Rails
// side. Named rather than inlined for the reason `NO_SELECTION` is: a MapLibre
// expression array is cast `as never` to satisfy the style types, so a typo in a
// literal inside one is caught by nothing at all.
export const COVERAGE_DRAWN = 'drawn'
export const COVERAGE_NAMED = 'named'
export const COVERAGE_NONE = 'none'

// A country whose appellations the atlas holds as NAMES, with no boundary for
// any of them: Chile's 80, Switzerland's 73, Serbia's 81. The same ink as a
// drawn country at under half the strength, and a DASHED line where the drawn
// one has a ribbon — the appellations are in the atlas and in the search box,
// and the map cannot draw them because no open source publishes a shape. A
// dash is how a map has always said "this edge is not surveyed".
export const NAMED_COUNTRY_FILL_COLOR = '#7f1d3f'
export const NAMED_COUNTRY_LINE_COLOR = '#7f1d3f'
export const NAMED_COUNTRY_LINE_DASH: [number, number] = [2.2, 1.6]

// A country the atlas has imported no appellations for at all: BARE GROUND.
//
// It was a neutral grey wash, so that an empty country read as "not imported
// yet" rather than as a broken map. Bare ground now means relief — the land is
// drawn, shaded, and visibly nobody's — and a grey film over a hillshade is the
// one thing that makes shading read as dirt. So nothing is laid on it: the
// fill's opacity is 0 (`COUNTRY_FILL_OPACITY`), and this colour is only what
// the hover dims it with. The key's row is an empty swatch with this border.
export const UNCOVERED_COUNTRY_FILL_COLOR = '#43302a'
export const UNCOVERED_COUNTRY_LINE_COLOR = '#bdb3ab'

// How strongly each of those three is drawn, and how it recedes.
//
// Strength follows meaning: a drawn country is the only strongly tinted land on
// the world view, a names-only country is under half of it, and a country with
// nothing is not tinted at all. The relief shows through both tints.
//
// And the whole layer recedes as the camera closes in, because from zoom 4 the
// appellations say what the country tint was saying, more precisely. It goes in
// two steps, `COUNTRY_RECEDE_ZOOMS[0]` to `[1]` and then on to `[2]`:
//
// - By the second stop a DRAWN country is gone (`COUNTRY_FILL_OPACITY_NEAR`).
//   That is earlier than it used to go, and on purpose: an appellation lays no
//   film on the ground any more, so a country tint still on the page at zoom 5
//   would be the only film left, under every ribbon in it.
// - A NAMED country keeps a trace to the end (`COUNTRY_FILL_OPACITY_DEEP`), so
//   Chile still reads as "held, not drawn" to a reader who has zoomed into it
//   and found no appellation to look at. Nothing is drawn over it for the
//   trace to stack under.
//
// The layer stays in the style at every zoom whatever its opacity: it is what
// a click or the pointer is tested against, and a country is still openable
// from inside it.
export const COUNTRY_FILL_OPACITY: Record<string, number> = {
  [COVERAGE_DRAWN]: 0.24,
  [COVERAGE_NAMED]: 0.11,
  [COVERAGE_NONE]: 0,
}
export const COUNTRY_FILL_OPACITY_NEAR: Record<string, number> = {
  [COVERAGE_DRAWN]: 0,
  [COVERAGE_NAMED]: 0.05,
  [COVERAGE_NONE]: 0,
}
export const COUNTRY_FILL_OPACITY_DEEP: Record<string, number> = {
  [COVERAGE_DRAWN]: 0,
  [COVERAGE_NAMED]: 0.05,
  [COVERAGE_NONE]: 0,
}
export const COUNTRY_RECEDE_ZOOMS: [number, number, number] = [2.5, 4.6, 6.5]

// The two edge marks stop where the country shapes would start to lie. The
// atlas's country polygons are simplified to about 22 km: under a pixel at the
// world view, five pixels at zoom 4, where the basemap is already drawing the
// surveyed border beside them. So both marks fade out over this stretch and
// their layers end at `COUNTRY_EDGE_MAX_ZOOM`.
export const COUNTRY_EDGE_OPACITY: Record<string, number> = {
  [COVERAGE_DRAWN]: 0.5,
  [COVERAGE_NAMED]: 0.85,
}
export const COUNTRY_EDGE_FADE_ZOOMS: [number, number] = [2.6, 3.6]
export const COUNTRY_EDGE_MAX_ZOOM = 4

// The ribbon's width, as `[zoom, px, …]`. It is inset by half of it, so its
// outer edge is the coast; the dashed line is under half as wide and inset the
// same way.
export const COUNTRY_RIBBON_WIDTH_STOPS = [1, 2, 3.5, 3.4]
export const COUNTRY_NAMED_LINE_WIDTH_STOPS = [1, 1, 3.5, 1.4]

// What pointing at a country adds to its fill, in the world view only — it
// fades to nothing over the first stretch of `COUNTRY_RECEDE_ZOOMS` with the
// fill itself. Zoomed in,
// the country under the pointer is the whole screen, and a screen that flushes
// every time the pointer leaves an appellation is a strobe, not a hover.
//
// Lower than it was, because it is now the WHOLE of what a country with no
// appellations shows under the pointer: 14% on bare ground was a country
// switching on. There it is a faint umber dimming rather than claret
// (`UNCOVERED_COUNTRY_FILL_COLOR`), so pointing at Brazil does not promise
// appellations the atlas does not have.
export const COUNTRY_HOVER_OPACITY_BOOST = 0.08

// The appellation layer, drawn over the countries.
export const REGION_SOURCE_ID = 'atlas-regions'
export const REGION_FILL_LAYER_PREFIX = 'atlas-regions-fill'
export const REGION_LINE_LAYER_PREFIX = 'atlas-regions-line'
// The names: ONE layer for every tier, like the ribbons (region_layer.ts has
// why — the order names are placed in). It was a layer per tier.
export const REGION_LABEL_LAYER_ID = 'atlas-regions-label'

// The ribbons: ONE layer for every tier, where the band it replaces was a layer
// per tier. One layer is the mechanism, not a saving — see `REGION_RIBBON`.
export const REGION_RIBBON_LAYER_ID = 'atlas-regions-ribbon'

// One POINT per appellation, which is what its name is drawn from. A symbol
// layer on the polygon source places a label in every tile the polygon touches,
// which is how "Temecula Valley" came to be written four times across one
// valley.
export const REGION_LABEL_SOURCE_ID = 'atlas-region-labels'

// The journal's own claret (`--color-accent` in application.css) for the
// hairline and the name, and the ink the ribbon's three tints are mixed from:
// an appellation is one ink at several weights.
export const REGION_FILL_COLOR = '#7f1d3f'
export const REGION_LINE_COLOR = '#7f1d3f'
export const REGION_LABEL_COLOR = '#7f1d3f'

// The paper a name is knocked out of, not white: a white halo on a chalk map is
// what makes a label look pasted on. It is the land with a little more white in
// it, because the ground under a name is shaded now, and a lit slope in the
// relief is lighter than flat chalk — between the two is the one value that is
// not a patch on either.
export const REGION_LABEL_HALO_COLOR = 'rgba(246,243,239,0.95)'

// What pointing at an appellation does to it: the SAME ink, heavier. The
// hairline goes to this deeper claret and thickens, the ribbon to
// `REGION_RIBBON_HOVER_COLOR` and widens — through feature-state, so it costs
// one repaint and no relayout, and it snaps rather than fades (MapLibre does
// not transition a data-driven value, which for a hover is the right behaviour
// anyway).
//
// It was `--color-accent-bright`, a second and hotter hue. On a plate with one
// claret and one amber, a third warm colour under the pointer read as a state
// of its own — half way to "selected" — and it is gone.
export const REGION_HOVER_COLOR = '#5c1230'
export const REGION_RIBBON_HOVER_COLOR = '#8a2c50'
export const REGION_RIBBON_HOVER_GROW = 1.45

// How many zoom levels a tier takes to fade in once the camera crosses its
// `min_zoom`. Every region paint opacity is a ramp from 0 at the tier to full
// this far past it, so the camera scrubs the reveal instead of a whole tier of
// France appearing between two frames.
export const REGION_REVEAL_ZOOMS = 0.35

// DEPTH IS RIBBONS INSIDE RIBBONS. This replaces KTD13's depth-by-opacity.
//
// Candiago's boundaries are municipality unions and nest by construction, so at
// zoom 7 four appellations — Bordeaux, Médoc, Haut-Médoc, Margaux — cover one
// commune. They used to be told apart by four stacked fills of 12% claret, each
// a darker patch inside the one under it. That worked as arithmetic and failed
// as a picture: the plate is drawn on shaded ground now, and three films of
// claret over a hillshade is a pink page with the hills taken out of it. Deep
// in a region the fills had already been made to recede to a third to stop
// exactly that, which left depth to the band and the hairline anyway.
//
// So an appellation lays NO film. It is a hairline, and inside the hairline a
// ribbon: a flat band hugging the boundary, the hand-tinted edge of a printed
// atlas plate. Depth is said by the ribbon, twice over, one entry here per
// level of nesting (0 an appellation, 1 one nested in it, 2 anything deeper):
//
// - `scale`: each level down the ribbon is NARROWER (`regionRibbonWidth`);
// - `tint`: and STRONGER. The tint is the claret the ribbon is printed in
//   before the whole layer is laid on the map at `REGION_RIBBON_LAYER_OPACITY`;
//   on chalk the three land at about 11%, 24% and 38% claret
//   (`ribbonStrength`). Width alone would not do: two ribbons of one tint
//   running along a shared edge are one wide ribbon.
// - `hair`: the hairline is a touch heavier for a parent than for its children.
//
// And they are ONE line layer, drawn through `line-layer-opacity`: every ribbon
// is painted opaque into an offscreen pass, children after their parents
// (`line-sort-key`), and the result is laid down once. So a ribbon never prints
// over itself at a join, a needle-thin spike or a shared edge; a child's ribbon
// REPLACES the strip of its parent's that it covers; and the parent shows
// beyond it. Three layers at 58% each would have accumulated wherever two
// boundaries coincide — and nested boundaries coincide all the time — which is
// the stacking this drawing exists to be rid of. The ground shows through at
// 42%, and the pass is the one piece of per-frame work the drawing adds (see
// region_layer.ts).
//
// The data has three tiers (`Geolocatable::NESTING_ZOOM_STEPS`), so the few
// appellations nested three and four deep are drawn as depth 2.
export interface RegionRibbonDepth {
  tint: string
  scale: number
  hair: number
}

export const REGION_RIBBON: readonly RegionRibbonDepth[] = [
  { tint: '#dcc2cb', scale: 1, hair: 1.12 },
  { tint: '#c495a7', scale: 0.8, hair: 1 },
  { tint: '#a8627c', scale: 0.66, hair: 0.9 },
]
export const REGION_RIBBON_LAYER_OPACITY = 0.58

// And how much of a shape a ribbon may take: this share of the radius of the
// largest circle that fits inside it, at most. Half leaves the middle half of
// even the smallest shape clean (see `regionRibbonPaint`).
export const REGION_RIBBON_ROOM_SHARE = 0.5

// A ribbon's width in px at depth 0, as `[zoom - tier, px]`: a hint when a tier
// first appears, a real margin once one appellation fills the screen, and no
// wider after that. Interpolated at base 1.5, which is `ribbonCurve` below.
export const REGION_RIBBON_WIDTH_STOPS: readonly [number, number][] = [
  [0, 1.5],
  [3, 5.6],
  [6, 10.4],
]
const REGION_RIBBON_BASE = 1.5

function ribbonCurve(past: number) {
  const stops = REGION_RIBBON_WIDTH_STOPS
  const last = stops[stops.length - 1]
  if (past <= stops[0][0]) return stops[0][1]
  if (past >= last[0]) return last[1]

  const next = stops.findIndex(([at]) => at > past)
  const [fromAt, fromWidth] = stops[next - 1]
  const [toAt, toWidth] = stops[next]
  const progress = (REGION_RIBBON_BASE ** (past - fromAt) - 1) / (REGION_RIBBON_BASE ** (toAt - fromAt) - 1)

  return fromWidth + (toWidth - fromWidth) * progress
}

// Which entry of `REGION_RIBBON` a level of nesting is drawn with.
export function ribbonDepth(depth: number) {
  return REGION_RIBBON[Math.max(0, Math.min(depth, REGION_RIBBON.length - 1))]
}

/**
 * How wide the ribbon of a tier is at a zoom, in px: a function of how far past
 * its own zoom the camera is, times the depth's scale.
 *
 * Both factors shrink going down — a child appears later than its parent, so it
 * is less far along the curve, and its scale is smaller — which is the
 * invariant the drawing rests on: at every zoom a child's ribbon is narrower
 * than its parent's, so it always lies INSIDE it and the parent shows beyond.
 */
export function regionRibbonWidth(tier: number, depth: number, zoom: number) {
  return Math.round(ribbonCurve(zoom - tier) * ribbonDepth(depth).scale * 100) / 100
}

/**
 * How much claret a depth's ribbon lands as on the chalk it is printed over:
 * the share of claret in its tint, times the opacity the layer is laid at. The
 * one number the key's swatches and the distinguishability test are both
 * written against — about 0.11, 0.24 and 0.38.
 */
export function ribbonStrength(depth: number) {
  const channel = (hex: string, at: number) => parseInt(hex.slice(at, at + 2), 16)
  const share = [1, 3, 5].map((at) => {
    const paper = channel(PLAIN_BACKGROUND_COLOR, at)

    return (paper - channel(ribbonDepth(depth).tint, at)) / (paper - channel(REGION_FILL_COLOR, at))
  })

  return (REGION_RIBBON_LAYER_OPACITY * (share[0] + share[1] + share[2])) / 3
}

// THE ONE EXCEPTION TO "NO FILM".
//
// For the first couple of zoom levels after it appears, an OUTERMOST
// appellation is twenty pixels across: too small to carry a ribbon that says
// anything, and at state zoom a hairline round nothing is a scribble. So for
// that stretch — and only that stretch — it has a faint claret tint, held for
// `REGION_FILL_HOLD_ZOOMS` past its own zoom and gone by
// `REGION_FILL_GONE_ZOOMS`. At the zoom where an appellation is a shape with
// ground inside it, the fill's opacity is 0.
//
// It is the old plateau at about half its strength, kept for the half of its
// job it was good at. The other half, depth, it no longer does at all: a
// NESTED appellation has no film of its own (region_layer.ts, `fillOpacity`).
// It had one, and two films of 6.5% stacked wherever a child sat in its
// parent — which between zoom 7 and 9 was every sub-appellation of Napa and
// Sonoma, pink on pink, on a plate whose rule is that nothing is filmed.
//
// The fill LAYERS stay in the style at every zoom whatever their opacity,
// because they are what a click or the pointer is tested against. That is the
// whole reason a layer that mostly draws nothing is still a layer per tier.
export const REGION_FILL_OPACITY = 0.065
export const REGION_FILL_HOLD_ZOOMS = 1.4
export const REGION_FILL_GONE_ZOOMS = 2.3

// What `depth` films of that opacity would composite to. Nothing on the map
// stacks them any more; the arithmetic is kept for the tests that say so.
export function stackedFillOpacity(depth: number) {
  return 1 - (1 - REGION_FILL_OPACITY) ** depth
}

// How deep the legend's nested swatch was drawn when depth was stacked fills.
export const NESTED_FILL_DEPTH = 2

/**
 * The stops of a tier's `fill-opacity` at rest, as `[zoom, opacity, …]` for a
 * linear interpolation: nothing at the tier's own zoom, the film a third of a
 * level later, the film still at the end of the hold, and nothing from
 * `REGION_FILL_GONE_ZOOMS` on — which is where it stays, because an
 * interpolation clamps past its last stop.
 */
export function regionFillStops(tier: number): number[] {
  return [
    tier,
    0,
    tier + REGION_REVEAL_ZOOMS,
    REGION_FILL_OPACITY,
    tier + REGION_FILL_HOLD_ZOOMS,
    REGION_FILL_OPACITY,
    tier + REGION_FILL_GONE_ZOOMS,
    0,
  ]
}

// The wash an appellation gains while the pointer is on it.
//
// The fill used not to answer the pointer at all, for a reason that still
// holds: an appellation is routinely bigger than the screen, and with the
// camera inside the South Coast, the pointer leaving Temecula Valley recoloured
// every pixel of the map and crossing back recoloured them again. But with no
// film on the ground, an appellation that IS a shape on the screen has nothing
// inside it to say "this one" except its edge, and at arm's length the edge of
// a small one is easy to lose. So the wash is back, and it is tied to how far
// past its own zoom the camera is, which is the nearest thing an expression has
// to "how big is this on screen":
//
// - while the tier is state-sized, a little more than the film it already has;
// - `REGION_HOVER_WASH_OPACITY` from where the film has gone, for as long as an
//   appellation of that tier is still seen whole;
// - and nothing from `REGION_HOVER_WASH_GONE_ZOOMS` past it, where a container
//   of that tier is the page. There the edge answers alone, as before, and a
//   container whose edge is off screen simply shows nothing — which is also
//   true: there is none of it to point at.
export const REGION_HOVER_FILM_OPACITY = 0.1
export const REGION_HOVER_WASH_OPACITY = 0.06
export const REGION_HOVER_WASH_HOLD_ZOOMS = 4
export const REGION_HOVER_WASH_GONE_ZOOMS = 6

/** The same stops as `regionFillStops`, for the feature under the pointer. */
export function regionHoverFillStops(tier: number): number[] {
  return [
    tier,
    0,
    tier + REGION_REVEAL_ZOOMS,
    REGION_HOVER_FILM_OPACITY,
    tier + REGION_FILL_HOLD_ZOOMS,
    REGION_HOVER_FILM_OPACITY,
    tier + REGION_FILL_GONE_ZOOMS,
    REGION_HOVER_WASH_OPACITY,
    tier + REGION_HOVER_WASH_HOLD_ZOOMS,
    REGION_HOVER_WASH_OPACITY,
    tier + REGION_HOVER_WASH_GONE_ZOOMS,
    0,
  ]
}

// The hairline: quiet when a tier first appears, where there are most of them
// in least room, and at nearly full strength by `REGION_LINE_NEAR_ZOOMS` past
// it, where it is the edge of something. Its width runs from
// `REGION_LINE_WIDTH[0]` at the tier's zoom to `[1]` six levels on, times the
// depth's `hair`; under the pointer it is `REGION_LINE_HOVER_WIDTH`.
//
// Neither it nor the ribbon recedes however deep the camera goes: they are
// what carries a boundary, and a boundary matters more the closer the camera
// is to it.
export const REGION_LINE_OPACITY = 0.6
export const REGION_LINE_NEAR_OPACITY = 0.9
export const REGION_LINE_NEAR_ZOOMS = 2.5
export const REGION_LINE_WIDTH: [number, number] = [0.6, 1.2]
export const REGION_LINE_HOVER_WIDTH: [number, number] = [1.5, 2.3]
export const REGION_LINE_WIDTH_ZOOMS = 6

// NAMES, IN THREE RANKS. One point per appellation, and how much room there is
// round that point — the radius of the largest circle that fits inside the
// shape there, in degrees of latitude — decides how it is lettered:
//
// - `REGION_LABEL_GREAT_ROOM` and over: the few great containers (North Coast,
//   Central Coast, Sierra Foothills), in spaced roman CAPITALS. They are the
//   chapter headings of the plate, and capitals are what lets a name stretch
//   across a region without reading as the name of the town under its middle.
// - `REGION_LABEL_WIDE_ROOM` and over: an appellation with room to spare, in
//   the italic a size up.
// - the rest: plain italic.
//
// Rank comes from the shape, not from the tier, because tier says how deep an
// appellation nests and nothing about how big it is: Sonoma Coast and a
// two-vineyard AVA are both tier 5. A bigger name is also placed first, so when
// two collide it is the small one that steps aside.
export const REGION_LABEL_GREAT_ROOM = 0.18
export const REGION_LABEL_WIDE_ROOM = 0.074

// The clear ground a name keeps round itself, in px, as `[zoom, px, …]`: twice
// as much at state zoom as among the vineyards. From California down to Napa
// the names used to be set edge to edge — every appellation that had a point
// free was lettered, each as loud as the next — and the largest are placed
// first, so what the wider berth thins is the small ones. From zoom 9 it is
// the 4 px it always was.
export const REGION_LABEL_PADDING_STOPS = [4, 9, 7, 7, 9, 4]

// How much clear ground a parent's name needs, in the same degrees, before it
// is moved off its own centre into the part none of its children cover. About
// 1.3 km. With nothing filled, a parent's name set on top of a child reads as
// the child's name; but a parent that is nearly all children has nowhere better
// to go, and its name stays where it was.
export const REGION_LABEL_FREE_ROOM = 0.012

// The other pop: a tier whose layers are added for the first time, because its
// features only just arrived. They are added transparent and eased up once the
// source has the data, over this long.
export const REGION_ARRIVAL_FADE_MS = 280

// THE PLACE THE OPEN PANEL DESCRIBES, AND THE ONLY AMBER ON THE MAP.
//
// It is drawn from a source of its own, not by repainting every tier, and what
// it draws changed with the plate. There is no film on the ground to tell the
// selected place from its neighbours by, so the selection is said the other way
// round:
//
// - everything that is NOT the place is dimmed, by a warm veil laid over the
//   roads and every other appellation's lines and under every label and dot
//   (`SELECTED_VEIL_*`). The source holds two features for it: the place, and
//   the world with the place cut out of it.
// - the place's own claret ribbon and hairline are switched off, by a
//   `selected` feature-state, and replaced by an opaque amber ribbon, an amber
//   line and a paper casing outside it.
// - NOTHING is laid on the ground inside. The inside of the selected place is
//   the one part of the screen at full strength, which is the point.
//
// It replaces a soft amber glow under the roads with a cased line over them.
// The glow lit the edge of a place whose inside was claret film like its
// neighbours'; with the film gone it was a place marked only by a blur.
//
// `SELECTED_FILL_COLOR` is the amber itself: the ribbon here, and the ring and
// halo of the selected winery dot. It keeps its name because the winery layer
// reads it; nothing it colours is a fill of an area any more.
export const SELECTED_FILL_COLOR = '#f59e0b'
export const SELECTED_LINE_COLOR = '#b45309'

// `--color-surface-raised`: the pale casing outside the selected line, which is
// what lets a 2 px amber stroke hold its edge across a road and a ribbon.
export const SELECTED_CASING_COLOR = '#f7f6f5'

// The veil. Warm and dark rather than grey, so dimmed ground is the same paper
// in shade and not a different paper; and at an eighth, which is enough to
// make the place the brightest thing on screen and not enough to hide a road
// or a neighbour's name.
export const SELECTED_VEIL_COLOR = '#43302a'
export const SELECTED_VEIL_OPACITY = 0.125

// The amber ribbon's width, as `[zoom, px, …]`; region_layer.ts insets it by
// half of it, so it lies inside the line exactly where the claret one did.
// Modest, and scaled by zoom rather than fixed: an open place is fitted to the
// screen, so a low zoom means a small polygon shown from far away — on a phone
// Temecula Valley is about fifty pixels across its waist — and a ribbon that
// meets itself in the middle of one is a slab again.
export const SELECTED_RIBBON_WIDTH_STOPS = [3, 2.4, 9, 4, 12, 6.5]
export const SELECTED_LINE_WIDTH_STOPS = [3, 1.3, 10, 2]
export const SELECTED_CASING_WIDTH_STOPS = [3, 3.2, 10, 5]

// A COUNTRY gets the veil and a soft amber BAND inside its edge, and no crisp
// line. Its polygon is simplified to about 22 km (see region_layer.ts), and a
// cased line traces that error across Brittany and the Gironde; a blurred band
// under the water, trimmed by the surveyed coast, does not. It needs no wash of
// its own any more — it had one, at 10%, because a glow along a coast was all
// that marked a selected France. The veil over everything else does that now.
//
// The band is twice as wide and twice as strong as it first was. At 40% and
// two or three pixels it measured 1.16 to 1 against the land it lay on: France
// read as selected only by the veil round it, and the key beside it showed an
// amber mark the map could not be seen to draw. At this strength its outer
// edge reaches the amber of an appellation's ribbon and it fades to nothing
// about ten pixels in — soft enough to forgive the 22 km, strong enough to be
// the mark the key says it is.
export const SELECTED_GLOW_OPACITY = 0.8
export const SELECTED_GLOW_WIDTH_STOPS = [1, 4, 3, 6, 6, 9, 11, 12]

export const SELECTED_SOURCE_ID = 'atlas-selected'
export const SELECTED_VEIL_LAYER_ID = 'atlas-selected-veil'
export const SELECTED_GLOW_LAYER_ID = 'atlas-selected-glow'
export const SELECTED_CASING_LAYER_ID = 'atlas-selected-casing'
export const SELECTED_RIBBON_LAYER_ID = 'atlas-selected-ribbon'
export const SELECTED_LINE_LAYER_ID = 'atlas-selected-line'

// Which of the selection source's two features one is. A property rather than
// two sources, because both change together, in one `setData`.
export const SELECTED_PART_VEIL = 'veil'

// Nothing is selected. A sentinel rather than null because a MapLibre `==`
// expression compares against a value, and no slug is ever the empty string.
// No paint expression compares against it any more — selection is a source and
// a feature-state now — but the shell still speaks it.
export const NO_SELECTION = ''

// THE MAP IS LETTERED IN FRAUNCES, the journal's own display serif: roman for a
// point (a town, a country, the count in a chip), italic for an area or a
// natural feature (an appellation, a sea, a river, a summit). That is how a
// printed atlas tells a wine region from the town inside it.
//
// The fontstack NAMES are still the two the glyph endpoint serves. OpenFreeMap
// serves exactly three — Noto Sans Regular, Bold and Italic — and nothing else;
// every other name, and every comma-joined stack, is a 404. So a `text-font` is
// always a one-element array naming one of the three, and the style's
// `font-faces` (lib/atlas_style.ts) says that the first two of those names are
// drawn from two files this app hosts, for the codepoints those files cover.
// Anything outside them — Latin Extended, Cyrillic, CJK — is asked of the
// endpoint under the same name, which is why the names are kept: a label with
// a character Fraunces lacks is still lettered, in Noto Sans, rather than
// asking for a stack nobody serves.
//
// This was an experiment behind a constant, off, for the appellation labels
// alone and from the chrome's variable file. Both halves of that were what was
// wrong with it: a variable file is always drawn at its display optical size
// (script/fonts/build_atlas_fonts.py), and one serif layer on a sans map read
// as a different product laid on top. It is two static cuts for every label
// now, and they REPLACE the two glyph ranges the opening view used to download
// rather than adding to them.
export const SERVED_FONTSTACKS = ['Noto Sans Regular', 'Noto Sans Bold', 'Noto Sans Italic'] as const

export const MAP_FONT_ROMAN = ['Noto Sans Regular']
export const MAP_FONT_ITALIC = ['Noto Sans Italic']

// The codepoints the two cuts are declared for: Basic Latin and Latin-1, the
// dotless i and the oe ligature the Latin subset also carries, and general
// punctuation. Every character an appellation name in today's atlas uses; a
// name with one that is not here (a Hungarian ő, a Polish ł) gets that one
// character from the glyph endpoint.
export const MAP_FONT_RANGES = ['U+0000-00FF', 'U+0131', 'U+0152-0153', 'U+2000-206F']

// What the map asks MapLibre for the moment a style has loaded, so that both
// files are fetched and ready to draw with before the first tile wants a
// letter (`primeLettering` in components/map/use_maplibre.ts, which says what
// that saves). One glyph of each stack is enough, because each stack is drawn
// from exactly one file.
//
// The stacks are named the way a tile names them when it asks — a layer's
// `text-font` joined by commas — because that string is the key MapLibre keeps
// its glyphs under. The character has to be one the files are DECLARED for
// (`MAP_FONT_RANGES`): any other is asked of the glyph endpoint instead, which
// would add a request to every load rather than save one.
export const MAP_FONT_PRIMED_STACKS = [MAP_FONT_ROMAN, MAP_FONT_ITALIC].map((font) => font.join(','))
export const MAP_FONT_PRIMER = 'a'

// An appellation's name: the italic, and the roman for the few lettered in
// capitals (`REGION_LABEL_GREAT_ROOM`).
export const REGION_LABEL_FONT = MAP_FONT_ITALIC
export const REGION_LABEL_CAPS_FONT = MAP_FONT_ROMAN

// The face for a count inside a cluster chip, the winery layer's and the
// journal's: the roman, which the opening view has already loaded for its
// country names, so a count costs no request. (It was Noto Sans Regular with a
// hairline halo to fake a semibold, because true Bold was a third glyph range
// on the boot path. The roman cut is drawn at weight 430 and needs less help.)
export const MARK_COUNT_FONT = MAP_FONT_ROMAN

// What the map falls back to when the basemap will not load: the same paper the
// bundled style is printed on (`land` in lib/atlas_style.ts), so a dead basemap
// looks like this atlas with the ink missing rather than like a different grey
// application.
export const PLAIN_BACKGROUND_COLOR = '#f1efea'

// The vines themselves, which the basemap draws from the tiles it already
// downloads: the one saturated ground colour on the plate, and so the one piece
// of ground a key has to name. They are here, and not only in the palette in
// lib/atlas_style.ts, for the reason the paper above is: the chrome draws a
// swatch of it, and the style is in the map's lazy chunk, which a swatch must
// not pull into the entry bundle. The style reads these.
//
// `VINEYARD_MIN_ZOOM` is the first zoom OpenMapTiles carries a vineyard at; the
// edge is drawn from where a block is a block (the style has the zoom).
export const VINEYARD_COLOR = '#b7ca8f'
export const VINEYARD_EDGE_COLOR = '#93a968'
export const VINEYARD_MIN_ZOOM = 8

// THE RELIEF: a warm hillshade under the paper, which is what makes the plate a
// picture of ground rather than a diagram on a sheet.
//
// It is this app's own asset, baked ahead of time and served from this origin:
// 512 px images, pre-toned so that flat ground in them is exactly
// `PLAIN_BACKGROUND_COLOR` (adding the layer shifts nothing that was flat),
// shadows go toward a warm umber and lit slopes toward paper white. Drawn over
// `RELIEF_TILE_SIZE` px tiles, so a relief pixel is always shown a little
// larger than it is — wash under engraving, which is the intent, and about
// half the images a 512 px grid would ask for. Levels 0 to `RELIEF_MAX_ZOOM`
// are baked; MapLibre stretches the deepest past that.
//
// IT IS NEVER ON THE BOOT PATH. The source is not in the style: the map adds
// it after its first `idle`, when the plate is already drawn (components/map/
// relief.ts). And a reader who asked their browser to save data — `Save-Data`,
// or `prefers-reduced-data` — does not get it at all.
//
// HOW STRONG, AND UNTIL WHEN: it follows THE DEEPEST IMAGE THERE IS FOR THE
// GROUND IN VIEW, not the zoom alone. The pyramid is uneven by design — the
// whole world to level `complete` (5 today), the deep levels only round an
// appellation — and a relief pixel is a picture of landform only while it is
// drawn near its own size. Stretched four times it is soft; stretched twelve it
// is grey cloud-shadow under crisp roads and names, which is what the Alps were
// at zoom 8 (level 5, stretched) and what Temecula was at zoom 12 (level 10).
// So the layer is at full strength to half a zoom past the deepest level in
// view, at half strength a zoom after that, and gone `RELIEF_SOFTEN_ZOOMS[2]`
// past it: over baked wine country that is 10.5, 11.5 and 12.3, and over
// ground nobody has baked it is 5.5, 6.5 and 7.3, where the plate goes back to
// flat chalk rather than to a blur. (It was one ramp for everywhere: full to
// zoom 10, 45% at 12.6, gone at 13.5.)
//
// `reliefOpacityStops(deepest)` is that ramp as `[zoom, opacity, …]`, and the
// layer ends at its last stop, so no image is asked for past it. Which level
// is the deepest in view is read from the manifest when the camera comes to
// REST (components/map/relief.ts) — never per frame; between two rests the
// ramp is a plain zoom expression. `RELIEF_FADE_MS` lets the hills develop
// instead of popping.
export const RELIEF_SOURCE_ID = 'atlas-relief'
export const RELIEF_LAYER_ID = 'atlas-relief'
export const RELIEF_TILE_PATH = '/relief/{z}/{x}/{y}.webp'
export const RELIEF_TILE_SIZE = 768
export const RELIEF_MAX_ZOOM = 10
export const RELIEF_SOFTEN_ZOOMS: [number, number, number] = [0.5, 1.5, 2.3]
export const RELIEF_FADE_MS = 240

export function reliefOpacityStops(deepest: number): number[] {
  const level = Math.max(0, Math.min(deepest, RELIEF_MAX_ZOOM))
  const [full, half, gone] = RELIEF_SOFTEN_ZOOMS
  // A touch under full at the world view, where the whole plate is relief and
  // the country tints are laid over it.
  const opening = level + full > 4 ? [1, 0.88, 4, 1] : [0, 1]

  return [...opening, level + full, 1, level + half, 0.5, level + gone, 0]
}

// What says which relief images exist: one small JSON document beside them.
//
// The pyramid is not whole and never will be — the world is baked to a shallow
// level and the deep levels only where there is an appellation — and a browser
// writes every 404 it is answered to the console, whatever the page does with
// the response. So the map does not find the holes by asking: it reads this
// once, after the first idle, and asks only for images it lists. An image it
// does not list is answered from the level above without a request
// (components/map/relief.ts has the shape of the document). The bake writes
// it, and the server answers this address even when nothing has been baked —
// with a manifest that lists nothing — so the one request the relief always
// makes is never a 404 either.
export const RELIEF_MANIFEST_PATH = '/relief/manifest.json'

// (The relief's credit, `RELIEF_ATTRIBUTION`, is further down, beside the path
// it links to.)

// R21's two journal layers, over the atlas polygons.
//
// THE READER'S OWN MARKS ARE CLARET DISCS. They were ink, on the reasoning that
// the atlas spent claret on its own marks and "a wine-toned pin would disappear
// into the country it sits on". That was true of a country filled solid. On
// this plate a country is a quarter-strength tint and an appellation is a
// hairline, and nothing the atlas draws is a solid claret DISC — while the one
// mark a tasting was drawn beside by the dozen, a winery whose size nobody
// states, is a dark neutral dot: `#2a0d16` against `#54454b` is 2 to 1, and a
// tasting among seventy producers was one more of them, a few pixels larger.
// Claret is also what the chrome already calls the journal's.
//
// And shape carries it as well as colour, because colour alone is not a key
// (winery_layer.ts has the same rule for its own dots):
//
// - one tasting is a RINGED disc — a claret core, a paper ring, a claret ring
//   outside that. No winery dot has a second ring.
// - several are a claret disc with their number in paper.
// - tastings the atlas could place no nearer than their COUNTRY are the same
//   disc turned inside out: paper, with a claret ring and numeral, set under
//   the country's name rather than on it (`JOURNAL_COUNTRY_DROP_PX`). A solid
//   disc in the middle of Kansas reads as a place in Kansas.
// - a wishlist item is the ring alone: wanted, not yet drunk.
export const TASTING_SOURCE_ID = 'journal-tastings'
export const WISHLIST_SOURCE_ID = 'journal-wishlist'

export const TASTING_PIN_COLOR = '#7f1d3f'
export const TASTING_PIN_RING_COLOR = '#f7f6f5'

// One tasting's three radii, in px: the core, the paper ring's outer edge and
// the claret ring's.
export const TASTING_PIN_RADII = { core: 3.5, paper: 6, ring: 8 } as const

// What an unfilled wishlist pin is filled with. It used to be fully transparent,
// which over a tinted country left a thin ring with wine showing through
// it — a mark that vanished exactly where wishlist items are — and over relief
// it would be a ring round a hillside. A translucent wash of the ring colour
// keeps it the UNFILLED one (the ground still shows) and keeps it findable.
export const WISHLIST_PIN_FILL_COLOR = 'rgba(247,246,245,0.6)'

// How big the disc for a number of tastings is drawn, as `[sqrt(count), px]`:
// by the square root, as a winery chip is, so it is the disc's area that grows
// with the count. ONE table for the layer's paint and for the arithmetic that
// keeps two discs off each other (components/map/journal_layers.ts) — they have
// to agree to the pixel. A step smaller than they were (14, 18 and 22 px): the
// reader's marks were the heaviest things on a signed-in map, heavier than the
// chips counting a region's producers.
export const JOURNAL_RADIUS_STOPS: readonly [number, number][] = [
  [1, 10],
  [3, 13],
  [7, 16],
  [15, 20],
]

// How far under its point a country-only disc is set, beyond its own radius.
// A country's representative point is, near enough, where the basemap letters
// the country's name, and a disc on it reads "SO●TH AFRICA".
export const JOURNAL_COUNTRY_DROP_PX = 14

// And how far under a place's NAME a mark for that place hangs, beyond its own
// radius, where the map is drawing the place: a line of lettering and a gap.
// The line is the second of two — most appellations are two words — at the
// largest the italic is set, with room for the tail of a "y": at 14 the disc
// under "Temecula Valley" touched it.
export const JOURNAL_NAME_DROP_PX = 17

// THE WINERY MARK: the reader's mark on a winery they have wines from. A
// claret dot on the winery's own dot, and beside it a small claret badge with
// how many of their wines are from there.
//
// It is not a counted disc, because it is not an approximation. A counted disc
// says "about here, this many"; a linked producer's wines are AT one known
// point, and the winery's own dot is already drawn there. So the mark sits on
// that dot, exactly, and the number stands off to the upper right where a
// badge on anything is looked for.
//
// - The dot is 5 px to its outer edge (3.5 and a 1.5 paper ring): small enough
//   that the amber ring a SELECTED winery wears, which starts at 5.5 px, still
//   shows round it. A ring-and-core pin the size of a single tasting's would
//   cover it. And a bare ring is the wishlist's mark.
// - The badge is always drawn, for one wine too: the dot and the number
//   together are the mark, so a "1" is not left looking like something else.
//   It widens at three figures.
//
// `WINERY_MARK_MIN_ZOOM` is where the mark starts being drawn. It mirrors
// `WineryLayerQuery::POINT_ZOOM`, where the winery layer turns from chips into
// dots: under that there is no dot to stand on, and the reader's wines there
// are an ordinary counted disc. The shell passes the server's own number
// (`map_zoom.winery_points`); this is what a map with no shell round it uses.
//
// `WINERY_MARK_REACH_PX` is how much room one mark is taken to need, as a
// radius, when two of them are tested for touching: the dot, the badge and its
// offset come to about 20 px across, so two marks whose centres are nearer
// than 26 px (twice this and a 2 px gap) cannot both be read, and are drawn as
// one ordinary counted disc that eases in on a click. At `MAX_ZOOM` 26 px is
// about a hundred metres, and two wineries nearer than that are never drawn
// apart: their disc opens the journal for the place they share instead
// (journal_layers.ts, `comesApart`).
export const WINERY_MARK_MIN_ZOOM = 8
export const WINERY_MARK_REACH_PX = 12
export const WINERY_MARK_DOT_RADIUS = 3.5
export const WINERY_MARK_RING_WIDTH = 1.5
export const WINERY_MARK_BADGE_RADIUS = 7.5
export const WINERY_MARK_BADGE_WIDE_RADIUS = 10
export const WINERY_MARK_BADGE_WIDE_FROM = 100
export const WINERY_MARK_BADGE_OFFSET: [number, number] = [9, -9]

// The ink a chip's soft shadow is cast in (`--color-ink`).
export const CHIP_SHADOW_COLOR = '#2a0d16'

export const JOURNAL_PATH = '/journal'
export const WISHLIST_PATH = '/wishlist'

export const SOURCES_PATH = '/sources'
// The small winery spotlight, which renders outside the map shell. Its own
// paths live in lib/spotlight_config.ts; this is only the chrome's way there.
export const SPOTLIGHT_PATH = '/spotlight'
export const MAP_ROOT_PATH = '/'

// The id of one source's card on the sources page, so a credit on the map can
// land a reader ON the source it credits rather than at the top of forty.
export function sourceAnchor(key: string) {
  return `source-heading-${key}`
}

// The credit for the elevation data the relief is shaded from, for the
// attribution line beside the basemap's. A second and separate obligation, as
// the winery data's is: the basemap's credit does not discharge it.
//
// THE WORDING IS THE DATASET'S, NOT OURS TO SHORTEN. Terrain Tiles is a
// composite of fifteen surveys and its attribution document lists eleven
// required statements; they are on the sources page in full, from
// `Atlas::SourceRegistry` (`terrain_tiles`), whatever was baked. What the map
// carries is the short credit that document's own best practice asks for —
// the dataset named, the largest contributors named, and a link to the rest
// (docs/atlas/relief.md, "Provenance"). It read "relief: USGS, SRTM" while the
// plate was American only; a world rollout owes Copernicus and the others a
// place on the line too.
//
// Parts, like the basemap's, joined by single spaces:
// "Relief: Terrain Tiles — USGS, NOAA, Copernicus and others". The link is to
// this app's own page, so the component draws it as a visit and not a new tab.
//
// The last part is a TAIL: where the map's bottom row cannot hold the whole
// line and a key beside it, the credit reads "Relief: Terrain Tiles" — the
// dataset named and linked to the card that prints every statement — and the
// contributors are named again from where the row has the room (the footer in
// layouts/atlas_layout.tsx). Never less than that, and never on the sources
// page, where the credit is always whole.
export const RELIEF_SOURCE_KEY = 'terrain_tiles'
export const RELIEF_ATTRIBUTION: readonly AttributionPart[] = [
  { label: 'Relief:' },
  { label: 'Terrain Tiles', href: `${SOURCES_PATH}#${sourceAnchor(RELIEF_SOURCE_KEY)}` },
  { label: '— USGS, NOAA, Copernicus and others', tail: true },
]

// The panel returns focus here on close, which is the only keyboard path back
// out of a canvas MapLibre has taken over.
export const MAP_CONTAINER_ID = 'atlas-map'

// Mirrors `Place::KIND_COUNTRY`. The panel branches on it to decide whether an
// empty children list means "no appellation data imported yet" or nothing at all.
export const COUNTRY_KIND = 'country'

// Six countries straddle the antimeridian, and a plain min/max over their
// longitudes reports the whole world (see Geolocatable.bounding_box). Fitting
// such a bbox would zoom the camera out to the globe, so anything this wide
// flies to the representative point instead, which is derived from the largest
// part and is correct.
export const GLOBAL_BBOX_LNG_SPAN = 180
export const PLACE_FLY_ZOOM = 6

// THE CAMERA LANDS IN THE PART OF THE MAP A READER CAN SEE.
//
// A fitted place used to be centred under the whole canvas with a uniform
// 64px margin, while the detail panel covered the right 408px of a desktop and
// the bottom 60dvh of a phone: Temecula's wineries sat under the panel, and on
// a phone the United States opened on Canada, because the point it flies to was
// behind the sheet. Every fit and every flight is now told what the chrome is
// covering (`lib/camera.ts`), and these are the chrome's sizes as built:
//
// - `DETAIL_PANEL_RESERVE_PX` is the header's `md:pr-[25.5rem]` in
//   layouts/atlas_layout.tsx — the panel's 24rem, its 0.75rem inset and a
//   0.75rem gap — in pixels. A different panel width means changing both.
// - `CHROME_TOP_PX` is the header's one 40px row and its padding from `md` up;
//   `CHROME_TOP_PHONE_PX` is the two rows a phone has.
// - On a phone the bottom is MEASURED where it can be: the layout hands the
//   camera the height of its dock, which is the sheet, the attribution riding
//   on top of it and any notice above that. It has to be measured because the
//   sheet is "up to" 60dvh and usually much less — France's is about 150px —
//   and a camera padded for the most it could be drew France in the top fifth
//   of the screen over four hundred pixels of the Sahara. The attribution is
//   counted because it is opaque enough to hide a boundary and sits exactly
//   where the bottom of a fitted place lands: Napa Valley's southern tip was
//   under it. (From `md` it is one line in the corner, and the margin below
//   already clears it.)
// - `DETAIL_SHEET_HEIGHT` (the sheet's `max-h-[60dvh]`) and `CREDIT_PHONE_PX`
//   (the attribution's three lines and the row's own padding — it was 44, for
//   two, until the relief's credit became too long to share a phone's line
//   with either of the others) are what the bottom is taken to be when there
//   is nothing to measure, which is the most it can be.
//
// `PLACE_FIT_PADDING` is then the margin around the place INSIDE what is left,
// and it is the smaller one wherever what is left is no wider than a phone: on
// a phone, where it is about 390 by 190, and on a tablet beside an open panel.
// `MIN_FIT_PX` is the floor: MapLibre refuses a fit whose padding leaves no
// room at all (it warns and does not move), so on a window too small for the
// chrome and a margin the margin goes first and the chrome's share is scaled.
export const DETAIL_PANEL_RESERVE_PX = 408
export const DETAIL_SHEET_HEIGHT = 0.6
export const CHROME_TOP_PX = 56
export const CHROME_TOP_PHONE_PX = 104
export const CREDIT_PHONE_PX = 56
export const PLACE_FIT_PADDING = 64
export const PLACE_FIT_PADDING_PHONE = 16
export const MIN_FIT_PX = 96

// A point is "on show" only this far inside the uncovered part. A dot whose
// centre is on the very edge of the panel is half under it.
export const VISIBLE_EDGE_PX = 24

// HOW LONG A FLIGHT TAKES.
//
// MapLibre's default flies at a fixed speed along a zoom-out-and-in curve, so
// its duration grows with distance and has no ceiling: measured in this app, a
// hop between two neighbouring appellations took 3.1 s and a deep link 4.2 s,
// with nothing fetched for the destination until it landed. A reader who
// clicked a place wants to be looking at it. So every move states its own
// duration: half a second for a nudge, a tenth of a second more for each zoom
// level crossed and for each doubling of the distance in screens, and never
// more than 1.1 s however far it is.
//
// NOT `maxDuration`. That option does not cap a flight, it replaces a flight
// that would exceed it with a jump (`camera.ts`: `duration = 0`).
export const FLIGHT_MIN_MS = 500
export const FLIGHT_MAX_MS = 1100
export const FLIGHT_STEP_MS = 100

// AND WHAT IS NOT A FLIGHT: a move that lands on the camera the map already
// has. It is not made at all (owner, 2026-10-01: a move that travels nowhere is
// not a flight; every move that travels keeps its duration and its easing).
//
// The case that was measured is the breadcrumb from a producer back to its
// appellation. It fits the box the map was built fitted to, with the padding
// it was built with, and MapLibre flew it all the same: half a second from
// `movestart` to `moveend` and about thirty full-map frames, to travel 0 px
// and ask for no tile, with the panel on screen 50 ms after the click.
//
// "The camera it already has" is said with a tolerance, because the same fit
// worked out twice need not agree to its last decimal: the two centres within
// a pixel of each other on the canvas, and the two zooms within a hundredth of
// a level — 0.7% of scale, five pixels at the edge of a 1440px canvas. Half a
// second spent on either is nothing a reader would see happen. A move that is
// past either one flies for as long as it always did.
export const SAME_CAMERA_PX = 1
export const SAME_CAMERA_ZOOM = 0.01

// Where the map opens for a producer, and what a search result for one flies
// to: close enough that the estate is a dot among its neighbours rather than a
// cluster's count, far enough out that the appellation's shape is still on
// screen around it.
//
// 11, where it was 12. At 12 the second half of that sentence was not true —
// Temecula Valley fits a screen at 10.6, and at 12 a producer's page showed a
// few streets of it with the valley's line and name off every edge — and it is
// past where the relief holds (`RELIEF_SOFTEN_ZOOMS`), so the page a reader is
// sent to straight from a search opened on flat paper. At 11 the estate's
// ringed dot is in its valley, on its hills, inside the amber line.
export const WINERY_CAMERA_ZOOM = 11

// The last camera the reader settled, kept for the life of the tab. The
// Journal, the Wishlist, Sources and a tasting all unmount the map shell on
// purpose, and coming back used to rebuild it at the world view however deep
// the reader had been. `sessionStorage` rather than `localStorage`: where you
// were looking is a fact about this sitting, not about the next one.
export const CAMERA_STORAGE_KEY = 'winemap:camera'

// The one thing "show my location" stores (lib/locate.ts), and it is a
// yes-or-no: the reader was asked where they are and said no, so the map does
// not raise the question on its own again. `localStorage`, unlike the camera
// above, because the point of remembering a refusal is not to ask again next
// week. Never a position: where a reader is stays in memory, on the map.
export const LOCATE_REFUSED_KEY = 'winemap:locate-refused'

// INTENT, BEFORE THE CLICK.
//
// A pointer that rests on a place or a producer, and a search result that
// becomes the active one, are both a reader about to open something. The page
// they would open is asked for then, so the click that follows is answered
// from memory rather than from the network.
//
// - The dwell is how long the pointer has to stay on ONE thing first. Without
//   it a pointer crossing California on its way to the search box would ask
//   for every appellation under its path; 90 ms is longer than a pointer in
//   motion spends on anything and shorter than the time between deciding to
//   click and the button going down.
// - The cache is 30 s, not the five minutes it is tempting to make it. A
//   prefetched page carries the shared props as they were when it was
//   fetched — the reader's own tasting and wishlist pins among them — and
//   Inertia only drops an entry by tag or wholesale. Log a wine, come back,
//   click a place hovered four minutes ago, and a long-lived entry would
//   re-apply the old pins and un-draw the new one. Thirty seconds is longer
//   than a hover-then-click and shorter than a trip to the journal and back.
export const PANEL_PREFETCH_DWELL_MS = 90
export const PANEL_PREFETCH_CACHE_MS = 30_000

// The pages that open as a panel over the map, by the names Rails renders them
// under. Each is a chunk of its own, and their code is asked for once the map
// has drawn (`warmPages`), so the first place a reader opens does not wait on
// a download the fifth would not.
//
// `PANEL_WARM_WAIT_MS` is how long that waits where the browser cannot say it
// is idle — Safari has no `requestIdleCallback` — and the longest it waits
// where the browser can. Long enough to stay out of the way of the map's own
// first tiles, short enough to be done before a reader has found something to
// click.
export const PANEL_PAGES = ['places/show', 'wineries/show', 'grapes/show'] as const
export const PANEL_WARM_WAIT_MS = 2_000

export function placePath(slug: string) {
  return `/places/${slug}`
}

// The map links to a tasting now (R21), so this lives beside placePath rather
// than being imported from journal_config -- which would pull the journal's
// module graph into the map's.
export function tastingPath(id: number) {
  return `/tastings/${id}`
}

export function grapePath(slug: string) {
  return `/grapes/${slug}`
}

// Every grape, as a document (`GrapesController#index`): the directory that
// leads to the pages above.
export const GRAPES_PATH = '/grapes'

// A place as a document of its own (`RegionsController`): the guide a search
// result lands on, and where the place panel sends a reader who wants to read
// rather than explore. Here beside `placePath`, which is the same record on
// the map, so the pair is declared once.
export const REGIONS_PATH = '/regions'

export function regionPath(slug: string) {
  return `/regions/${slug}`
}

// R10's deep link. A path, never a query parameter: the `127.0.0.1` redirect
// rebuilds the URI from path parameters only and drops the query string (KTD15).
export function wineryPath(slug: string) {
  return `/wineries/${slug}`
}

// The two ways out of a winery's panel into the journal, for a reader who has
// an account: the add form with this winery already chosen (the form fills in
// the producer and pins the wine here), and the journal narrowed to the
// reader's own producer for it. Spelled here, beside `tastingPath`, for its
// reason: these are the map's links, and the constants module is the one
// thing the map and the journal both import.
export function newTastingAtWineryPath(slug: string) {
  return `/tastings/new?winery=${encodeURIComponent(slug)}`
}

export function journalProducerPath(producerSlug: string) {
  return `${JOURNAL_PATH}?producer=${encodeURIComponent(producerSlug)}`
}

// Below this a query is a keystroke rather than an intent, and every row in both
// tables would match. Mirrors AtlasSearch::MINIMUM_QUERY_LENGTH: the server
// enforces it, and the client honours it so a keystroke costs no round trip.
export const SEARCH_MIN_QUERY_LENGTH = 2

// Long enough that a word typed at speed is one request rather than seven,
// short enough that the answer reads as attached to the keystroke.
//
// It was 200, and the wait a reader felt was this plus the round trip. A fast
// typist's keys land 80 to 110 ms apart, so 120 still swallows the middle of a
// word; a slower one now gets an answer per pause, which is what a slower
// typist is looking at the list for. Each request is a scan the server answers
// in a few milliseconds, behind a per-address rate limit sized for typing.
export const SEARCH_DEBOUNCE_MS = 120

// KTD5's viewport layer.
//
// How many features the shell holds across every frame it has visited. It
// bounds the merged set, evicting least-recently-seen first, so an hour of
// panning across Europe does not accumulate the whole continent in memory and
// in every redraw. It is NOT a `history.state` bound: the merged set lives in
// React state and is never written back into the page object.
//
// Three frames' worth, NOT one. It used to equal `MapProps::DETAIL_FEATURE_CAP`
// exactly, which quietly defeated the merge at the boundary: a single capped
// response fills the client set on its own, so the next frame evicted a feature
// of the first for every feature it added — panning at the cap blanked what the
// reader had just been looking at, which is the behaviour merging exists to
// prevent. It has to sit above one server response, not level with it.
export const REGION_FEATURE_CAP = 1_800

// How long after the camera comes to rest the appellation frame is asked for.
//
// It was 250, on the reasoning that "a zoom gesture settles several times as it
// eases". MapLibre 6 does not do that: `moveend` fires ONCE per gesture —
// measured for a burst of wheel notches, a slow trackpad stream, a flicked drag
// and a drag released at rest — and after a wheel zoom it already fires about
// 415 ms after the last notch. So the 250 was dead time on top of a signal that
// had already waited, and the regions for a frame arrived two thirds of a
// second after the reader stopped.
//
// What is left is the smallest wait that still folds a BURST of settles into
// one request, because two real sources of bursts remain: a window being
// dragged to a new size reports a settle per resize, and so does a key held
// down on the map. 50 ms is three frames — under anything a reader perceives as
// a pause, over the gap between two reports of one burst.
export const REGION_FETCH_DEBOUNCE_MS = 50

// How long a fetch may take before it announces itself. A settle that resolves
// inside this window shows no badge at all, so panning does not flicker
// "Loading regions…" over the map once per frame.
export const REGION_PENDING_DELAY_MS = 400

// The winery layer keeps the same silence for the same reason, and for as long.
// It had no wait of its own: the notice was mounted the moment the request left
// and a CSS animation delay kept it invisible — which holds only while
// animations run, so a reader who asked for reduced motion saw "Loading
// wineries…" on every pan. A timer holds for everyone.
export const WINERY_PENDING_DELAY_MS = REGION_PENDING_DELAY_MS

// How long after a winery fetch is called a success the shell waits before
// deciding the layer never arrived (see the layout). The two come out of one
// Inertia update and land within a frame or two of each other; this is that,
// with room.
export const WINERY_ANSWER_GRACE_MS = 100

// Four decimals is about 11 metres, far finer than a polygon simplified at the
// overview tolerance can express, and it keeps the frame key stable enough that
// a camera coming to rest twice in the same place is one request.
const FRAME_PRECISION = 4
const ZOOM_PRECISION = 2

// The `bbox` parameter, in the order `Geolocatable#intersecting_bbox` reads it.
export function viewportBbox(viewport: MapViewport) {
  return [viewport.west, viewport.south, viewport.east, viewport.north]
    .map((degree) => degree.toFixed(FRAME_PRECISION))
    .join(',')
}

export function viewportZoom(viewport: MapViewport) {
  return viewport.zoom.toFixed(ZOOM_PRECISION)
}

// What "the same frame" means: for skipping a repeat request, and for deciding
// whether a response has been outrun by the camera that asked for it.
export function viewportKey(viewport: MapViewport) {
  return `${viewportBbox(viewport)}@${viewportZoom(viewport)}`
}

// The winery layer, drawn over the countries and the appellations. One source
// for both modes: the server decides whether it is sending cells or dots, and
// each feature says which it is, so a mode change is a `setData` rather than a
// second source.
export const WINERY_SOURCE_ID = 'atlas-wineries'
export const WINERY_CLUSTER_LAYER_ID = 'atlas-wineries-cluster'
export const WINERY_CLUSTER_COUNT_LAYER_ID = 'atlas-wineries-cluster-count'
export const WINERY_POINT_LAYER_ID = 'atlas-wineries-point'

// Two layers that carry no data of their own and answer no click: the soft
// shadow that lifts a cluster chip off the map, and the amber halo behind the
// one dot an open panel describes.
export const WINERY_CLUSTER_SHADOW_LAYER_ID = 'atlas-wineries-cluster-shadow'
export const WINERY_HALO_LAYER_ID = 'atlas-wineries-halo'

// A third: the room each dot keeps clear of town names. It draws nothing at
// all — a symbol layer whose only job is to be in the way when the basemap's
// names are placed, so that a town's name steps to one side of the producers
// standing on it instead of being printed under them (winery_layer.ts).
export const WINERY_KEEPOUT_LAYER_ID = 'atlas-wineries-keepout'

// The feature kinds the layer draws. A property rather than two sources,
// because `mode` already tells the client which it is about to be handed and a
// filter on one source is cheaper than adding and removing another.
export const WINERY_CLUSTER_KIND = 'cluster'
export const WINERY_POINT_KIND = 'winery'

// KTD11's bucket, spelled the same on both sides of the wire. `null` is what the
// database stores and what the prop carries; this is what the map expression and
// the filter checkbox compare against, because a MapLibre `match` cannot match
// on null.
export const WINERY_UNKNOWN = 'unknown'

// Mirrors `Winery::SIZE_CLASSES` and `Winery::INDEPENDENCE_STATUSES`, smallest
// first, because the panel and the filters both render in this order and a
// sorted constant is one fewer place for that order to be re-declared.
export const SIZE_CLASSES = ['micro', 'small', 'mid', 'large']
export const INDEPENDENCE_STATUSES = ['independent', 'group_owned']

// R28's buckets in words. The numbers are the requirement's own, restated here
// because "mid" on its own is a label nobody can act on.
export const SIZE_CLASS_LABELS: Record<string, string> = {
  micro: 'Micro — under 1,000 cases a year',
  small: 'Small — 1,000 to 10,000 cases',
  mid: 'Mid — 10,000 to 50,000 cases',
  large: 'Large — over 50,000 cases',
  // "Not stated", the one name this state has: the plate's key, a producer's
  // Size row and this label all say it. It was "Unknown" here and "not stated"
  // there — two words for one state on one panel.
  [WINERY_UNKNOWN]: 'Not stated',
}

export const INDEPENDENCE_STATUS_LABELS: Record<string, string> = {
  independent: 'Independent',
  group_owned: 'Group-owned',
  [WINERY_UNKNOWN]: 'Unknown',
}

// A sequential ramp in one hue, deliberately not the claret the place layers
// use: a winery dot and an appellation's ribbon are different kinds of thing
// and should not read as two shades of the same claim. Teal is used for
// nothing else on the map.
//
// One step darker than it was. The ramp was tuned against a saturated street
// map; on chalk paper its lightest member, `#99f6e4`, was close to invisible,
// and a derived dot draws at 45% of that.
export const WINERY_SIZE_COLORS: Record<string, string> = {
  micro: '#5eead4',
  small: '#14b8a6',
  mid: '#0f766e',
  large: '#134e4a',
}

// KTD11's neutral: a value with its own legend line, not a gap. It is almost
// every winery on the map, so it has to look deliberate rather than unpainted —
// which is why it is an ink from the journal's own family rather than a stone
// grey from a palette the rest of the map no longer uses.
//
// A step darker than `--color-ink-soft`, which it used to be. A dot sits on
// chalk and on shaded relief now, and against the relief's deepest shadow
// (`#b1a89d`) soft ink was 2.9 to 1 — a dot on a north slope was hard to find.
// This is 3.8 to 1 there and 7.8 to 1 on flat chalk: darker than any ground a
// dot can be on, and still not the ink a tasting pin is.
export const WINERY_UNKNOWN_COLOR = '#54454b'

// HOW NEAR A MISS IS STILL A HIT. A dot is about eleven pixels across with its
// ring and stands on an appellation that is clickable too, so a press beside it
// used to open the place under it — another page altogether. A pointer within
// this many pixels of a mark, and on no mark, is on the nearest one
// (`hitsAt` in use_maplibre.ts, which also has what it costs): a dot then
// answers within about 24 px, and a thumb, which is wider and cannot hover to
// check, within about 36.
export const MARK_REACH_PX = 8
export const MARK_REACH_COARSE_PX = 14

// The pale ring every winery dot is drawn inside, and the face of a cluster
// chip: `--color-surface-raised`, the same paper the chrome's labels are cut
// from.
export const WINERY_STROKE_COLOR = '#f7f6f5'

// The ring a dot gains while the pointer is on it: the journal's ink.
export const WINERY_HOVER_STROKE_COLOR = '#2a0d16'

// KTD6's second axis on the map dot, and the only place in this frontend where a
// visual-only difference is unavoidable — a dot has no room for the word the
// badge carries. After P6c almost every coloured dot is an inference from a
// catalogue count or a platform, at 0.15 to 0.35 confidence, and drawing it
// identically to a 0.90 stated figure is the misreading this exists to prevent.
//
// Opacity rather than an outline, because the point layer already sets a pale
// `circle-stroke-color`: a second outline would collide with that stroke
// instead of reading as a new signal. P6a's KTD11 established a solid neutral
// colour for `unknown`, NOT an outline convention, so there is no
// outline-versus-fill vocabulary here to reuse and this axis is introduced
// deliberately, with its own legend line.
//
// 0.45 rather than something subtler: the dot is a few pixels across, and a
// difference a reader has to hunt for is a difference that is not being made.
export const WINERY_POINT_OPACITY = 1
export const WINERY_DERIVED_POINT_OPACITY = 0.45

// The legend line for that axis. The panel stays the authoritative explanation
// for any dot a reader questions; this is what stops a faded dot reading as a
// rendering fault, the same job `unknown`'s own line does.
export const WINERY_DERIVED_LEGEND_LABEL = 'Winery — size class estimated, not stated'

// Clusters are counts, not classes — a cell holding both a micro and a large
// producer has no class to be coloured by, so it is coloured as the thing it is.
//
// A cluster is a pale CHIP with a teal ring and a dark teal number, not the
// solid teal disc it was. A 12 to 34 px disc in the size ramp's own hue read as
// one enormous winery of the `mid` class; a chip reads as a label, which is what
// a count is. `WINERY_CLUSTER_COLOR` is the ring, and the chip's face is
// `WINERY_STROKE_COLOR`.
export const WINERY_CLUSTER_COLOR = '#0f766e'
export const WINERY_CLUSTER_LABEL_COLOR = '#134e4a'

// How far a click on a cluster eases in. Two levels quarters the area, which is
// enough to break most cells apart without the camera leaving the reader behind.
export const WINERY_CLUSTER_ZOOM_STEP = 2

// The ODbL obligation for the winery DATA, which is a second and separate
// obligation from the basemap's own attribution and is not discharged by it.
// Rendered whenever the winery layer is visible; `/wineries/:slug` discharges it
// separately, from the source's own registry entry, because that page is
// reachable without the layer ever having been switched on.
export const WINERY_DATA_ATTRIBUTION = '© OpenStreetMap contributors'

// Mirrors `WinerySizeEstimate::ESTIMATION_METHOD_STATED`. The one method in the
// vocabulary that is not an inference, which is why it is named rather than
// left as a literal: KTD6's whole rule is "stated, or else qualified", and the
// badge, the map dot and the legend all turn on this one string.
export const ESTIMATION_METHOD_STATED = 'stated'

// The method behind one size estimate, in plain words (R32). A reader cannot act
// on "platform_prior"; the whole point of the panel is that a badge can be
// traced back to the sentence or the inference that produced it.
// `vineyard_area` names its ASSUMPTION, not just its input, and that is the one
// label that has to. Every other method here reads something the producer did —
// a figure it stated, wines it lists, a group it belongs to. This one multiplies
// a planted area by a yield nobody measured for this producer, so a reader told
// only "estimated from the vineyard area" would reasonably take the number for
// something the winery published about itself. It is also the method most likely
// to be wrong for a reason the quote cannot show: hand-checking P6d's sample
// found the misses were farms whose acreage is not all vines — a muscadine
// fresh-produce grower, a two-ranch estate, a holding split between wine and
// cider.
export const ESTIMATION_METHOD_LABELS: Record<string, string> = {
  stated: 'A figure the producer states, read from a quoted sentence',
  vineyard_area: 'Converted from the area of vines it farms, at an assumed yield',
  group_member: 'Inferred from membership of a wine group',
  sku_count: 'Estimated from how many wines it lists for sale',
  distribution_footprint: 'Estimated from how widely it is distributed',
  platform_prior: 'Inferred from the sales platform it uses',
}

// The same treatment for independence evidence, keyed by the signal's `kind`.
// The vocabulary grows a member per phase: P6a produced one kind, P6b adds five.
// A reader cannot act on `ttb_owner_cluster`.
export const SIGNAL_KIND_LABELS: Record<string, string> = {
  // The field research ledger's: a sentence about who owns the producer, read
  // by hand from the producer's own pages and quoted. It had no entry, so the
  // panel printed the kind itself — `field_research` — as its explanation.
  field_research: 'A statement about who owns it, quoted from the producer’s own pages',
  wikidata_owner: "Wikidata's owner statement",
  federation_member: "An independent growers' federation certifying a member against its charter",
  champagne_badge: "A grower badge on the Comité Champagne's own producer listing",
  group_brand: "The brand named on a wine group's own portfolio page",
  ttb_sole_permit:
    'The only federal wine permit filed under its owner name — a population-level prior, not a claim about this ' +
    'producer',
  ttb_owner_cluster: 'One of four or more federal wine permits filed under one owner',
  grower_association_member:
    "A growers' association listing a member — the body represents winegrowers but publishes no charter " +
    'defining independence, so this is a first-party claim about the producer one step to the side of the one ' +
    'the badge makes',
}

/**
 * The override for a kind whose label depends on WHICH source wrote it, keyed by
 * `${kind}/${source_key}`.
 *
 * **This exists because getting it wrong is an R32 failure rather than a
 * cosmetic one.** The Vignerons Indépendants and FIVI importers both write kind
 * `federation_member` — deliberately, because the fact each publishes is the
 * same fact, and a kind per federation would have cost six more when P6e added
 * the readable CEVI federations — which is why those six write two kinds between
 * them rather than six. But `SIGNAL_KIND_LABELS` is keyed by kind alone, so a single
 * entry renders a FIVI member's evidence as a Vignerons Indépendants
 * membership: one federation's certification attributed to another, on the one
 * panel that exists so a badge can be traced back to who certified it.
 *
 * `source_key` is what already distinguishes them inside the evidence identity
 * `(winery_id, kind, source_key, group_slug)`, and the prop carries it — so the
 * label is resolved from it rather than from a second kind.
 *
 * **Keyed by kind AND source, not by source alone**, and the TTB importer is the
 * live demonstration: it writes `ttb_sole_permit` and `ttb_owner_cluster` under
 * one source key, and those two rows argue for OPPOSITE statuses. A
 * source-keyed map would collapse them into one sentence.
 */
export const SIGNAL_SOURCE_LABELS: Record<string, string> = {
  'federation_member/vignerons_independants':
    'A Vignerons Indépendants de France membership — the federation certifies its members against its charter: ' +
    'grow your own grapes, vinify them in your own cellar, sell the wine yourself',
  'federation_member/fivi':
    'A FIVI membership — the Federazione Italiana Vignaioli Indipendenti certifies its members against the same ' +
    'charter: grow your own grapes, vinify them in your own cellar, sell the wine yourself',
  'federation_member/asvei':
    'An ASVEI membership — the Association Suisse des Vignerons Encaveurs Indépendants, whose name is its ' +
    'definition: a vigneron-encaveur grows their own grapes and vinifies them in their own cellar',
  'federation_member/vindependent':
    'A Vindependent membership — the Hungarian association whose own philosophy defines the independent producer ' +
    'as one who accompanies every step of growing, making and selling',
  'federation_member/opvi_luxembourg':
    'An OPVI membership — the Organisation professionnelle des vignerons indépendants Privatwënzer Lëtzebuerg, ' +
    "which is Luxembourg's private growers as distinct from the cooperative",
  'grower_association_member/belgische_wijnbouwers':
    'A vzw Belgische Wijnbouwers membership — a sector association covering professional and semi-professional ' +
    'Belgian winegrowers. It publishes no independence charter',
  'grower_association_member/vignerons_wallonie':
    "An Association des Vignerons de Wallonie membership, on a row the association's own table marks as a " +
    'winegrower. It publishes no independence charter',
  'grower_association_member/smoe_greece':
    'A Σύνδεσμος Μικρών Οινοποιών Ελλάδος membership — the Association of Small Winemakers of Greece. Small is ' +
    'not the same claim as independent, and the association publishes no charter defining the latter',
}

export function signalKindLabel(kind: string, sourceKey: string): string {
  return SIGNAL_SOURCE_LABELS[`${kind}/${sourceKey}`] ?? SIGNAL_KIND_LABELS[kind] ?? kind
}

// The certification families `Winery::CERTIFICATION_SCHEMES` holds, in words.
// Only the first two derive a filterable column: `hve` is France's Haute Valeur
// Environnementale, `sustainable` is Champagne's own programme, and
// `environmental` exists precisely because the label that produces it does not
// say which scheme it means. The panel renders the producer's OWN label beside
// these, so "Vin Biologique" and "Organic farming" both stay visible as the
// words the estate published.
export const CERTIFICATION_SCHEME_LABELS: Record<string, string> = {
  organic: 'Organic',
  biodynamic: 'Biodynamic',
  hve: 'Haute Valeur Environnementale',
  sustainable: 'Sustainable viticulture',
  environmental: 'Environmental certification',
}

// `Winery::VISITING_OPTIONS`, in the server's order: most open to least. The
// filter lists them in this order and the panel labels its door card from it.
export const WINERY_VISITING_OPTIONS = ['walk_in', 'appointment', 'members_only', 'invite_only', 'not_open'] as const

export const WINERY_VISITING_LABELS: Record<string, string> = {
  walk_in: 'Walk-ins welcome',
  appointment: 'By appointment',
  members_only: 'Members only',
  invite_only: 'By invitation only',
  not_open: 'Not open to visitors',
  [WINERY_UNKNOWN]: 'Unknown',
}

// The door policies that make a visit an occasion rather than a stop — the small,
// private producers this map exists to find. The filter offers them as one
// shortcut and the panel gives them the special card.
export const WINERY_VISITING_SPECIAL = ['invite_only', 'members_only', 'appointment'] as const

// Why a door-policy bucket is empty, said to a reader. Every visiting value
// comes from a quoted page, researched by hand, so an empty bucket is exactly
// this and not a missing phase.
export const WINERY_VISITING_PHASE = 'None recorded yet.'

// `WineryFact::FARMING_PRACTICES`.
export const FARMING_LABELS: Record<string, string> = {
  organic: 'Organic',
  biodynamic: 'Biodynamic',
  dry_farmed: 'Dry-farmed',
  sustainable: 'Sustainable',
  regenerative: 'Regenerative',
}

// The platforms `WineryDtcChannel::PLATFORMS` names, cased the way each vendor
// cases itself. Without this the panel renders the stored constant, so a
// producer with a cellar door reads as `tasting_room` — which is the same defect
// as an unlabelled `distribution_footprint`, one table over.
export const DTC_PLATFORM_LABELS: Record<string, string> = {
  vinoshipper: 'Vinoshipper',
  commerce7: 'Commerce7',
  winedirect: 'WineDirect',
  orderport: 'OrderPort',
  shopify: 'Shopify',
  corksy: 'Corksy',
  tock: 'Tock',
  cellarpass: 'CellarPass',
  // The one member that names no vendor: selling at the cellar door is a
  // channel, not a platform, which is why the label is a place rather than a
  // company.
  tasting_room: 'Tasting room',
}

// HOW a channel was found (R30), which R32 needs for the same reason the
// estimate list does: "the platform's own feed said so" and "a script tag on the
// homepage looked like it" are different claims and a reader tracing a wrong
// badge has to be able to tell them apart.
//
// The stated-versus-inferred split runs through this vocabulary exactly as it
// runs through `ESTIMATION_METHOD_LABELS`, and the wording says so in the same
// voice: "Read from" for something a source answered directly, "Inferred from"
// for something we concluded. A fingerprint and `platform_prior`'s "Inferred
// from the sales platform it uses" are the same weakness seen from two angles,
// and a reader should not have to work that out.
export const DTC_DETECTED_BY_LABELS: Record<string, string> = {
  api: "Read from the platform's own feed",
  fingerprint: "Inferred from markup on the winery's own site",
  link_on_third_party: 'Seen as a link on another site we were allowed to read',
  storefront_link: "A storefront link published on the winery's own site",
}

/**
 * MINIMUM CONFIDENCE, AS THREE NAMED STEPS A READER CAN CHOOSE BETWEEN.
 *
 * The derivation writes five confidences across two axes (0.30, 0.35, 0.55,
 * 0.70, 0.90 — `WineryLayerQuery::CONFIDENCE_STEPS`), and the control used to
 * offer all five as radios labelled with the bare number and, per axis, the
 * weakest evidence each admitted in the importers' own words: "(0.35) By
 * ownership: A badge, a permit cluster or an association's listing" — the same
 * sentence under two different options, on a public atlas. Nobody outside the
 * project can choose between 0.3 and 0.35.
 *
 * What a reader can choose between is HOW SURE: anything at all, something
 * that makes it likely, or the producer's own word. So the control offers
 * those three, keyed by the threshold each sends, and the numbers stay in the
 * request. The two thresholds in between are still answered by the server and
 * are simply not offered.
 *
 * It is still a stepped selector and not a slider, for the reason it always
 * was: a continuous control would offer a thousand positions resolving to a
 * handful of result sets.
 */
export const CONFIDENCE_STEPS: readonly { threshold: number; label: string; says: string }[] = [
  { threshold: 0.55, label: 'Likely', says: 'a register, a badge or a membership listing says so' },
  { threshold: 0.9, label: 'Stated', says: 'the producer, or the federation that certifies it, says so in writing' },
]
export const CONFIDENCE_ANY_LABEL = 'Any evidence'

// WHY A BUCKET IS EMPTY, SAID TO A READER (KTD11).
//
// A disabled control says why it is off rather than merely that it is. What it
// says used to be the project talking to itself: "P6c records direct sales for
// United States producers only, and no phase yet names the rest of the world",
// and for the biodynamic filter a shell command, `bin/rails import:demeter`.
// That was written when the atlas had one reader, who ran the importers. It is
// public now, and a reader of an atlas is owed one plain sentence: nothing of
// this kind has been recorded yet, and — where there is one — the plain reason.
// Which sources are imported, and which are not and why, is the sources page's
// to say, at length.
//
// Plain is not the same as vague, and the facts in the old sentences were
// true and stay: a size has three origins and not one, and direct sales are
// recorded for one country. "Has not been collected yet" beside a United
// States producer whose channels WERE looked for would be the next untruth.
//
// The two said on two surfaces are constants, for the reason they were
// hoisted: this map's disabled controls and `winery_panel.tsx` both say them,
// and the next change to either moves one string.
export const WINERY_SIZE_PHASE =
  'A size comes from a figure the producer states, a United States sales catalogue or a planted area, and most ' +
  'producers publish none of the three.'

export const WINERY_DIRECT_SALES_PHASE = 'Direct sales are recorded for United States producers only so far.'

// The map itself, keyed by the filter value. Every entry is a whole sentence;
// the fallback (in winery_filters.tsx) covers a vocabulary member a later
// source adds before this map does.
export const WINERY_FILTER_PHASES: Record<string, string> = {
  micro: `No sizes recorded yet. ${WINERY_SIZE_PHASE}`,
  small: `No sizes recorded yet. ${WINERY_SIZE_PHASE}`,
  mid: `No sizes recorded yet. ${WINERY_SIZE_PHASE}`,
  large: `No sizes recorded yet. ${WINERY_SIZE_PHASE}`,
  independent: 'No independent producers recorded yet.',
  group_owned: 'No group-owned producers recorded yet.',
  organic: 'No organic certifications recorded yet.',
  biodynamic: 'No biodynamic certifications recorded yet.',
  direct_sales: WINERY_DIRECT_SALES_PHASE,
  walk_in: WINERY_VISITING_PHASE,
  appointment: WINERY_VISITING_PHASE,
  members_only: WINERY_VISITING_PHASE,
  invite_only: WINERY_VISITING_PHASE,
  not_open: WINERY_VISITING_PHASE,
  wine_club: 'wine clubs come from the field research ledger, one quoted winery page at a time',
  confidence: 'No producer has evidence this strong yet.',
}

/**
 * What an ENABLED control says, where a disabled one says why it is off.
 *
 * Same affordance, same file, opposite state — and it exists because after P6b
 * the independence data is not merely incomplete, it is **lopsided**. Roughly
 * 7,300 of ~24,400 wineries carry independence evidence and almost all of them
 * are French or Italian; the organic bucket is almost entirely French; the
 * United States has a few hundred permit matches and no charter evidence at all.
 *
 * A facet reading `independent 7,312` says nothing about where those rows are.
 * Kieran filtering for independent producers in California sees almost nothing
 * and cannot tell whether that means there are none or that the evidence has not
 * been imported yet — which is exactly the question `UNCOVERED_COUNTRY_FILL_COLOR`
 * answers for a country with no appellations, and "None declared" answers on the
 * sources page. This is the same move for a filter that now selects.
 */
export const WINERY_FILTER_COVERAGE: Record<string, string> = {
  independent:
    'Charter evidence covers France, Italy, Switzerland, Hungary and Luxembourg; a growers-association listing ' +
    'covers Belgium and Greece at lower confidence; and a federal-permit prior covers the United States. ' +
    'Bulgaria, Czechia, Slovakia, Slovenia, Spain and Portugal publish no directory this atlas can read, and ' +
    'the sources page says why for each. Elsewhere a producer reads as unknown.',
  group_owned:
    'Evidence so far is the brand portfolios the large groups publish themselves, and clusters of US federal ' +
    'permits. A group-owned label outside those two reads as unknown.',
  organic:
    'Certifications so far are French — Vignerons Indépendants badges and the Comité Champagne listing. A ' +
    'producer with no badge is unknown here, never conventional.',
  biodynamic:
    'Certifications come from the Demeter register worldwide, and from Vignerons Indépendants badges and the ' +
    'Comité Champagne listing in France. Demeter certifies operators rather than only producers, so each ' +
    "certification names the operator it was issued to. A producer with no badge is unknown here, never " +
    'conventional.',
}

// Tailwind's `md` breakpoint, restated because a media query cannot be read out
// of a class name. The detail panel is a bottom sheet below it and a side panel
// above, so this is exactly the width at which the filter disclosure and an open
// panel stop being able to share the screen. The camera reads the same width
// (lib/camera.ts) to know which of the two the panel is.
export const PHONE_MAX_WIDTH_PX = 767
export const PHONE_MEDIA_QUERY = `(max-width: ${PHONE_MAX_WIDTH_PX}px)`
